import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, formatApiError, useAuth } from "@/contexts/AuthContext";
import TranscriptEditor from "@/components/dashboard/TranscriptEditor";
import { startRecorder, stopRecorder, recorderDraft, subscribeRecorder,
  discardRecorderDraft, importRecorderFile } from "@/lib/recordingCapture";

const BASE = API + "/recordings";
const errorText = (error) => formatApiError(error.response?.data?.detail || error.message || "Request unavailable.");

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = name; document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function RecordingsPage() {
  const { user } = useAuth();
  const ownerKey = user?.id + ":" + (user?.active_workspace || user?.id);
  const own = !user?.active_workspace || user.active_workspace === user?.id;
  const [, render] = useState(0);
  const [opened, setOpened] = useState(false);
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [reports, setReports] = useState([]);
  const [history, setHistory] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [draftUrl, setDraftUrl] = useState("");
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("auto");
  const [reportLanguage, setReportLanguage] = useState("nl");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sequence = useRef(0);
  const reportRequest = useRef(null);
  const tasks = useRef(new Map());
  const draft = recorderDraft(ownerKey);
  const recording = ["starting", "recording", "stopping"].includes(draft?.status);

  useEffect(() => subscribeRecorder(() => render((value) => value + 1)), []);
  useEffect(() => () => { sequence.current += 1; stopRecorder(ownerKey); }, [ownerKey]);
  useEffect(() => {
    if (!draft?.blob) { setDraftUrl(""); return; }
    const url = URL.createObjectURL(draft.blob); setDraftUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [draft?.blob]);
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  async function list() {
    const { data } = await axios.get(BASE + "/audio");
    if (!Array.isArray(data.recordings)) throw new Error("Recording list unavailable.");
    setRecords(data.recordings); return data.recordings;
  }
  async function open() {
    if (busy) return;
    setBusy(true); setError("");
    try { await list(); setOpened(true); }
    catch (failure) { setError(errorText(failure)); }
    finally { setBusy(false); }
  }
  async function loadTranscript(id) {
    const [text, report] = await Promise.all([
      axios.get(BASE + "/transcripts/" + encodeURIComponent(id)),
      axios.get(BASE + "/transcripts/" + encodeURIComponent(id) + "/reports"),
    ]);
    setTranscript(text.data); setReports(report.data.reports || []); setHistory(null);
  }
  async function select(row) {
    if (busy || recording) return;
    if (selected && !window.confirm("Open another recording? Save or discard any open transcript edit first.")) return;
    const current = ++sequence.current;
    setBusy(true); setError(""); setTranscript(null); setReports([]); setHistory(null);
    setAudioUrl(""); setSelected(row); reportRequest.current = null;
    try {
      const response = await axios.get(BASE + "/audio/" + row.id + "/file", { responseType: "blob" });
      if (current !== sequence.current) return;
      setAudioUrl(URL.createObjectURL(response.data));
      if (row.transcript_id) await loadTranscript(row.transcript_id);
    } catch (failure) { if (current === sequence.current) setError(errorText(failure)); }
    finally { if (current === sequence.current) setBusy(false); }
  }
  async function upload() {
    if (!draft?.blob || busy || recording || !title.trim()) return;
    setBusy(true); setError("");
    try {
      const form = new FormData();
      form.append("request_id", draft.requestId); form.append("title", title.trim());
      form.append("file", draft.blob, draft.filename);
      await axios.post(BASE + "/audio", form);
      await list(); discardRecorderDraft(ownerKey); setTitle("");
    } catch (failure) { setError(errorText(failure) + " Your local audio is preserved."); }
    finally { setBusy(false); }
  }
  async function transcribe() {
    if (!selected || busy) return;
    if (!window.confirm("Transcribe this recording using the configured speech service? Review the literal transcript against the audio before approving it.")) return;
    const key = selected.id + ":transcribe";
    if (!tasks.current.has(key)) tasks.current.set(key, crypto.randomUUID());
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(BASE + "/audio/" + selected.id + "/transcribe",
        { request_id: tasks.current.get(key), language });
      await loadTranscript(data.transcript_id); await list();
      setSelected((previous) => ({ ...previous, transcript_id: data.transcript_id, transcription_state: "complete" }));
    } catch (failure) { setError(errorText(failure)); }
    finally { setBusy(false); }
  }
  async function correct(id, payload) {
    if (busy) throw new Error("Another recording operation is in progress.");
    setBusy(true);
    try {
      const { data } = await axios.patch(BASE + "/transcripts/" + transcript.id + "/passages/" + id, payload);
      await loadTranscript(transcript.id); return data;
    } catch (failure) { throw new Error(errorText(failure)); }
    finally { setBusy(false); }
  }
  async function splitPassage(id, payload) {
    if (busy) throw new Error("Another recording operation is in progress.");
    setBusy(true);
    try {
      const { data } = await axios.post(BASE + "/transcripts/" + transcript.id + "/passages/" + id + "/split", payload);
      await loadTranscript(transcript.id); return data;
    } catch (failure) { throw new Error(errorText(failure)); }
    finally { setBusy(false); }
  }
  async function approve(revision) {
    if (busy) throw new Error("Another recording operation is in progress.");
    setBusy(true);
    try {
      const { data } = await axios.post(BASE + "/transcripts/" + transcript.id + "/approve", { expected_revision: revision });
      await loadTranscript(transcript.id); return data;
    } catch (failure) { throw new Error(errorText(failure)); }
    finally { setBusy(false); }
  }
  async function generateReport() {
    if (!transcript || transcript.status !== "approved" || busy) return;
    const key = transcript.id + ":" + transcript.revision + ":" + reportLanguage;
    if (reportRequest.current?.key !== key) reportRequest.current = { key, id: crypto.randomUUID() };
    setBusy(true); setError("");
    try {
      await axios.post(BASE + "/transcripts/" + transcript.id + "/report",
        { request_id: reportRequest.current.id, language: reportLanguage });
      await loadTranscript(transcript.id);
    } catch (failure) { setError(errorText(failure)); }
    finally { setBusy(false); }
  }
  async function download(path, name) {
    setError("");
    try {
      const { data } = await axios.get(BASE + path, { responseType: "blob" });
      saveBlob(data, name);
    } catch { setError("Download unavailable. Refresh the recording and try again."); }
  }
  async function reloadCurrent() {
    if (busy || !transcript) return;
    setBusy(true);
    try { await loadTranscript(transcript.id); }
    catch (failure) { setError(errorText(failure)); }
    finally { setBusy(false); }
  }
  async function versions() {
    if (busy) return;
    try { const { data } = await axios.get(BASE + "/transcripts/" + transcript.id + "/versions"); setHistory(data); }
    catch (failure) { setError(errorText(failure)); }
  }
  function printReport(report) {
    const frame = document.createElement("iframe");
    frame.style.position = "fixed"; frame.style.width = "0"; frame.style.height = "0";
    frame.title = "Print meeting report"; document.body.appendChild(frame);
    const doc = frame.contentDocument;
    doc.open(); doc.write("<!doctype html><html><head><title>Vergaderverslag</title><style>body{font:11pt Georgia,serif;margin:18mm;color:#111}pre{white-space:pre-wrap;overflow-wrap:anywhere;font:inherit;line-height:1.5}h1{font-size:20pt}</style></head><body></body></html>"); doc.close();
    const heading = doc.createElement("h1"); heading.textContent = "Vergaderverslag"; doc.body.appendChild(heading);
    const source = doc.createElement("p"); source.textContent = "Bronversie: " + report.approval_id; doc.body.appendChild(source);
    const body = doc.createElement("pre"); body.textContent = report.text; doc.body.appendChild(body);
    setTimeout(() => { frame.contentWindow.focus(); frame.contentWindow.print(); setTimeout(() => frame.remove(), 60000); }, 100);
  }
  if (!own) return <p>Recording management is currently available to the active company's workspace owner.</p>;
  return <main className="max-w-5xl space-y-6">
    <header><h1 className="text-2xl font-semibold">Recordings &amp; Reports</h1>
      <p className="mt-2 text-sm">Record or upload audio, review the literal transcript and approve it before generating a professional report. Nothing is sent automatically.</p></header>
    {error && <p role="alert" className="rounded border border-amber-200 p-3">{error}</p>}
    {!opened ? <button disabled={busy} onClick={open} className="zy-btn-primary">Open recordings</button> : <>
      <section className="space-y-3 rounded-xl border bg-white p-4">
        <h2 className="font-semibold">New recording</h2>
        <p className="text-sm">Microphone audio only: this does not capture remote meeting participants automatically. Maximum upload size: 24 MB. Save parts separately for longer meetings.</p>
        <label className="block text-sm">Recording title<input value={title} maxLength={200} disabled={busy}
          onChange={(event) => setTitle(event.target.value)} className="mt-1 block w-full rounded border p-2" /></label>
        <div className="flex flex-wrap gap-3">
          <button disabled={busy || recording} onClick={async () => {
            if (draft?.blob && !window.confirm("Replace the unsaved local audio? Download it first if you want to keep it.")) return;
            try { await startRecorder(ownerKey); } catch (failure) { setError(failure.message); }
          }} className="rounded border px-3 py-2">Start microphone recording</button>
          <button disabled={!recording} onClick={() => stopRecorder(ownerKey)} className="rounded border px-3 py-2">Stop recording</button>
          <label className="rounded border px-3 py-2">Upload existing audio
            <input type="file" className="block text-sm" accept=".webm,.ogg,.wav,.mp3,.m4a,.mp4,.flac" disabled={busy || recording}
              onChange={(event) => {
                const file = event.target.files?.[0]; event.target.value = "";
                if (!file) return;
                if (file.size > 24 * 1024 * 1024 || !file.size) { setError("Choose nonempty audio up to 24 MB."); return; }
                if (draft?.blob && !window.confirm("Replace the unsaved local recording?")) return;
                try { importRecorderFile(ownerKey, file); } catch (failure) { setError(failure.message); }
              }} />
          </label>
        </div>
        <p role="status" className={recording ? "font-semibold text-red-700" : "text-sm"}>
          {draft?.status === "recording" ? "● Recording — microphone is on" : draft?.status === "starting" ? "Waiting for microphone permission…" : draft?.status === "stopping" ? "Finishing recording…" : "Microphone recording is off"}
        </p>
        {draft?.error && <p role="alert">{draft.error}</p>}
        {draftUrl && <><audio controls src={draftUrl} className="w-full" />
          <div className="flex flex-wrap gap-3">
            <button disabled={busy || recording || !title.trim()} onClick={upload} className="zy-btn-primary">Save original recording</button>
            <button onClick={() => saveBlob(draft.blob, draft.filename)} className="rounded border px-3 py-2">Download local audio copy</button>
          </div></>}
      </section>
      <section className="space-y-3">
        <div className="flex gap-3"><h2 className="font-semibold">Saved recordings</h2>
          <button disabled={busy} onClick={open} className="text-sm underline">Refresh</button></div>
        <p className="text-xs">Up to 100 most recent recordings.</p>
        {records.map((row) => <button key={row.id} disabled={busy || recording} onClick={() => select(row)}
          className="block w-full rounded border bg-white p-3 text-left">
          {row.title} · {new Date(row.created_at).toLocaleString()} · {row.transcription_state}
        </button>)}
      </section>
      {selected && <section className="space-y-4 rounded-xl border p-4">
        <h2 className="text-xl font-semibold">{selected.title}</h2>
        {audioUrl && <button onClick={() => download("/audio/" + selected.id + "/file", selected.filename)} className="rounded border px-3 py-2">Download original audio</button>}
        {!transcript && <><audio controls src={audioUrl || undefined} className="w-full" />
          <label className="block text-sm">Spoken language<select value={language} onChange={(event) => setLanguage(event.target.value)} className="ml-2 rounded border p-2">
            {[["auto", "Detect language"], ["nl", "Nederlands"], ["en", "English"], ["es", "Español"], ["de", "Deutsch"], ["fr", "Français"]].map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select></label>
          <button disabled={busy || !audioUrl} onClick={transcribe} className="zy-btn-primary">Create rough transcript</button>
          <p className="text-sm">Recognition can miss words or speaker changes. Labels identify consecutive text passages, not recognised identities. Check every passage against the original audio.</p>
        </>}
        {transcript && <><TranscriptEditor key={transcript.id} transcript={transcript} audioUrl={audioUrl}
          onCorrect={correct} onSplit={splitPassage} onApprove={approve} externalBusy={busy} onReload={reloadCurrent} />
          <div className="flex flex-wrap gap-3">
            {["docx", "pdf", "txt"].map((format) => <button key={format} onClick={() => download(
              "/transcripts/" + transcript.id + "/export?version=raw&file_format=" + format, "raw-transcript." + format)} className="rounded border px-3 py-2">Raw {format.toUpperCase()}</button>)}
            <button onClick={versions} className="rounded border px-3 py-2">Correction history</button>
          </div>
          {transcript.status === "approved" && <div className="space-y-3">
            <h3 className="font-semibold">Approved transcript and report</h3>
            <div className="flex flex-wrap gap-3">{["docx", "pdf", "txt"].map((format) => <button key={format} className="rounded border px-3 py-2"
              onClick={() => download("/transcripts/" + transcript.id + "/export?version=approved&file_format=" + format, "approved-transcript." + format)}>Approved {format.toUpperCase()}</button>)}</div>
            <label>Report language<select value={reportLanguage} onChange={(event) => setReportLanguage(event.target.value)} className="ml-2 rounded border p-2">
              {[["nl", "Nederlands"], ["en", "English"], ["es", "Español"], ["de", "Deutsch"], ["fr", "Français"]].map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
            <button disabled={busy} onClick={generateReport} className="zy-btn-primary ml-3">Generate report from approved text</button>
          </div>}
          {history && <section className="space-y-2"><h3 className="font-semibold">Correction history</h3>
            {history.edits.map((entry, index) => <article key={index} className="rounded border p-3 text-sm">
              <p>Passage {entry.after.turn} · {new Date(entry.at).toLocaleString()}</p>
              <p>Before: {entry.before.speaker_label} — {entry.before.text}</p>
              <p>After: {entry.after.speaker_label} — {entry.after.text}</p>
            </article>)}
            <p>{history.approved_versions.length} preserved approved version(s).</p>
            {history.approved_versions.map((version) => <details key={version.id} className="rounded border p-3">
              <summary>Approved source version {version.source_revision} · {new Date(version.at).toLocaleString()}</summary>
              <div className="space-y-3 pt-3">{version.passages.map((row) => <p key={row.id} className="whitespace-pre-wrap">
                {row.speaker_label}: {row.text}
              </p>)}</div>
              <div className="mt-3 flex gap-3">{["docx", "pdf", "txt"].map((format) => <button key={format} className="rounded border px-3 py-2"
                onClick={() => download("/transcripts/" + transcript.id + "/export?version=approved&approval_id=" + version.id + "&file_format=" + format, "approved-version." + format)}>Download {format.toUpperCase()}</button>)}</div>
            </details>)}</section>}
          {reports.map((report, index) => <article key={report.report_id || index} className="space-y-3 rounded border p-4">
            <h3 className="font-semibold">Generated report · {new Date(report.created_at).toLocaleString()}</h3>
            <p className="text-xs">Based on approval {report.approval_id}. Check this source version before sharing.</p>
            <p className="whitespace-pre-wrap break-words">{report.text}</p>
            <div className="flex flex-wrap gap-3">{["pdf", "docx", "txt"].map((format) => <button key={format} className="rounded border px-3 py-2"
              onClick={() => download("/reports/" + report.report_id + "/export?file_format=" + format, "meeting-report." + format)}>Download {format.toUpperCase()}</button>)}
              <button onClick={() => printReport(report)} className="rounded border px-3 py-2">Print report</button></div>
          </article>)}
        </>}
      </section>}
    </>}
  </main>;
}
